import React, { useRef } from 'react';

import * as xlsx from 'xlsx';
import { UncontrolledTooltip } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { EyeIcon, EyeOffIcon, UploadIcon } from '@iconicicons/react';

import { iocActions, selectIOCVisibility } from './iocSlice';
import { selectAllPoints } from '../map/slice/mapSelectors';
import { useClientPagination, useTheme } from '../helpers/hooks';
import { toIsoDate } from '../utils/dateUtils';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useLazyGetRawIOCContractQuery } from '../services/edgeApi';
import { MissingPointBadge, PointBadge } from '../helpers/PointBadge';
import { numberFormatter } from '../helpers/formatters';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { IOCContract } from '@/types';

export type IOCTableProps = {
  iocContracts: IOCContract[];
  gasDate: Date;
  onShowRawData: (rawContract: any) => void;
  isLoading?: boolean;
};

export const IOCTable: React.FC<IOCTableProps> = ({
  gasDate,
  onShowRawData,
  iocContracts,
  isLoading,
}) => {
  const dispatch = useDispatch();

  const [rawIOCTrigger] = useLazyGetRawIOCContractQuery();
  const allPoints = useSelector(selectAllPoints);
  const visibility = useSelector(selectIOCVisibility);

  const {
    slice: paginatedIocContracts,
    exhausted,
    getNext,
  } = useClientPagination(iocContracts, 30);

  const [theme] = useTheme();

  const tableRef = useRef<HTMLTableElement>(null);

  const downloadIocData = () => {
    const headers = [
      'Contract Number',
      'Shipper Name',
      'Agent Name',
      'TSP Name',
      'Rate Schedule',
      'Direction',
      'Loc Name',
      'Receipt Zone',
      'Delivery Zone',
      'Point Volume',
      'Max TRP',
      'Start Date',
      'End Date',
    ];

    const data = iocContracts.flatMap<Array<string | number | null>[]>(
      (contract) => {
        let acc: Array<string | number | null>[] = [];
        acc = acc.concat(
          contract.receipt_volumes.map(({ point: pointId, vol }) => {
            const point = allPoints[pointId];
            return [
              contract.k_id,
              contract.owner,
              contract.agent_name,
              contract.tsp.name,
              contract.raw_rate_schedule,
              'R',
              point.name,
              point.receipt_zone_name,
              point.delivery_zone_name,
              vol,
              contract.max_daily_quantity,
              new Date(contract.start_date).toLocaleDateString(),
              new Date(contract.end_date).toLocaleDateString(),
            ];
          }),
        );
        acc = acc.concat(
          contract.delivery_volumes.map(({ point: pointId, vol }) => {
            const point = allPoints[pointId];
            return [
              contract.k_id,
              contract.owner,
              contract.agent_name,
              contract.tsp.name,
              contract.raw_rate_schedule,
              'D',
              point.name,
              point.receipt_zone_name,
              point.delivery_zone_name,
              vol,
              contract.max_daily_quantity,
              new Date(contract.start_date).toLocaleDateString(),
              new Date(contract.end_date).toLocaleDateString(),
            ];
          }),
        );
        return acc;
      },
    );

    const ws = xlsx.utils.json_to_sheet([headers, ...data], {
      skipHeader: true,
    });
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, 'Index of Customers');
    const fileName = `IOC_${toIsoDate(gasDate)}.xlsx`;
    xlsx.writeFile(wb, fileName);
  };

  const setVisibility = (contracts: IOCContract[], visible: boolean) => {
    dispatch(iocActions.setVisibility({ contracts, visible }));
  };

  const showRawData = async (contract: IOCContract) => {
    try {
      const res = await rawIOCTrigger(contract.id).unwrap();
      onShowRawData(res);
    } catch (error) {
      console.error('Failed to fetch raw IOC contract data:', error);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-end p-3">
        <UploadIcon
          height={32}
          role="button"
          id="download-ioc-data-button"
          onClick={downloadIocData}
        />
        <UncontrolledTooltip target="download-ioc-data-button">
          Export to Excel
        </UncontrolledTooltip>
      </div>
      <table className="table" ref={tableRef}>
        <thead
          style={{
            position: 'sticky',
            top: 0,
          }}
        >
          <tr>
            <th>
              {iocContracts.some((contract) => visibility[contract.id]) ? (
                <EyeIcon
                  role="button"
                  onClick={() => setVisibility(iocContracts, false)}
                />
              ) : (
                <EyeOffIcon
                  role="button"
                  onClick={() => setVisibility(iocContracts, true)}
                />
              )}
            </th>
            <th>Contract Number</th>
            <th>Shipper Name</th>
            <th>Agent Name</th>
            <th>TSP Name</th>
            <th>Receipt Points</th>
            <th>Delivery Points</th>
            <th>Rate Schedule</th>
            <th>Max TRP</th>
            <th>Contract Start</th>
            <th>Contract End</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={11} className="text-center">
                <LoadingIndicator />
              </td>
            </tr>
          )}
          {paginatedIocContracts.map((contract) => {
            return (
              <tr key={contract.id.toString()}>
                <td className="text-center">
                  {visibility[contract.id] ? (
                    <EyeIcon
                      role="button"
                      onClick={() => setVisibility([contract], false)}
                    />
                  ) : (
                    <EyeOffIcon
                      role="button"
                      onClick={() => setVisibility([contract], true)}
                    />
                  )}
                </td>
                <td>
                  <span role="button" onClick={() => showRawData(contract)}>
                    {contract.k_id}
                  </span>
                </td>
                <td>{contract.owner}</td>
                <td>{contract.agent_name}</td>
                <td>{contract.tsp.name}</td>
                <td>
                  {contract.receipt_volumes.map(({ point, vol }) => (
                    <PointBadge
                      point={allPoints[point]}
                      extra_text={`${numberFormatter(vol, 0)} Dth`}
                      key={`${contract.id}-receipt-point-${point}-${vol}`}
                      theme={theme}
                    />
                  ))}
                  {contract.missing_points.receipt.map((point) => (
                    <MissingPointBadge
                      name={point.name}
                      extra_text={`${numberFormatter(
                        point.volume ?? 0,
                        0,
                      )} Dth`}
                      theme={theme}
                      key={`${
                        contract.id
                      }-missing-receipt-point-${point.loc_id.toString()}`}
                    />
                  ))}
                </td>
                <td>
                  {contract.delivery_volumes.map(({ point, vol }) => (
                    <PointBadge
                      point={allPoints[point]}
                      extra_text={`${numberFormatter(vol, 0)} Dth`}
                      key={`${contract.id}-delivery-point-${point}-${vol}`}
                      theme={theme}
                    />
                  ))}
                  {contract.missing_points.delivery.map((point) => (
                    <MissingPointBadge
                      name={point.name}
                      extra_text={`${numberFormatter(
                        point.volume ?? 0,
                        0,
                      )} Dth`}
                      theme={theme}
                      key={`${
                        contract.id
                      }-missing-delivery-point-${point.loc_id.toString()}`}
                    />
                  ))}
                </td>
                <td>{contract.raw_rate_schedule}</td>
                <td>{numberFormatter(contract.max_daily_quantity, 0)}</td>
                <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                <td>{new Date(contract.end_date).toLocaleDateString()}</td>
              </tr>
            );
          })}
          {!isLoading && paginatedIocContracts?.length === 0 && (
            <tr>
              <td colSpan={11} className="text-center">
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!isLoading && iocContracts?.length > 0 && !exhausted && (
        <InfiniteScrollObserver onScrollIn={getNext} />
      )}
    </div>
  );
};
